import { useEffect, useMemo, useRef, useState } from "react";
import { clamp, cx } from "./utils";

export interface WaveformProps {
  /** Precomputed peaks in 0–1 (one per bar), or an AudioBuffer to downsample. */
  peaks?: Float32Array | null;
  /** Alternative source; downsampled to `barCount` peaks on mount. */
  audioBuffer?: { getChannelData: (ch: number) => Float32Array; length: number } | null;
  /** Total audio duration in seconds — needed for the playhead and seeking. */
  durationSec?: number;
  /** Current playhead position in seconds. */
  currentSec?: number;
  /** Click/drag to seek; receives seconds. */
  onSeek?: (seconds: number) => void;
  /** Canvas height in px. @default 96 */
  height?: number;
  /** Target number of bars when downsampling an AudioBuffer. @default 400 */
  barCount?: number;
  /** Accessible description. @default "波形" */
  label?: string;
  className?: string;
}

/** Downsample a channel into `count` absolute-max peaks in 0–1. */
export function computePeaks(data: Float32Array, count: number): Float32Array {
  const out = new Float32Array(count);
  if (data.length === 0) return out;
  const block = data.length / count;
  for (let i = 0; i < count; i += 1) {
    const start = Math.floor(i * block);
    const end = Math.min(data.length, Math.floor((i + 1) * block));
    let max = 0;
    for (let j = start; j < end; j += 1) {
      const v = Math.abs(data[j] ?? 0);
      if (v > max) max = v;
    }
    out[i] = max;
  }
  return out;
}

function readVar(el: Element, name: string, fallback: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}

/**
 * Canvas waveform drawn with token colors. Resizes with its container via
 * ResizeObserver, shows a playhead and reports click-to-seek positions.
 */
export function Waveform({
  peaks,
  audioBuffer,
  durationSec = 0,
  currentSec = 0,
  onSeek,
  height = 96,
  barCount = 400,
  label = "波形",
  className,
}: WaveformProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(0);

  const data = useMemo<Float32Array>(() => {
    if (peaks && peaks.length) return peaks;
    if (audioBuffer) return computePeaks(audioBuffer.getChannelData(0), barCount);
    return new Float32Array(0);
  }, [peaks, audioBuffer, barCount]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap || width <= 0) return;
    const ctx = canvas.getContext?.("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(width * dpr));
    canvas.height = Math.max(1, Math.floor(height * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const bg = readVar(wrap, "--gz-surface-interactive", "#eef0f4");
    const bar = readVar(wrap, "--gz-action-primary", "#2f6bff");
    const barPast = readVar(wrap, "--gz-fg-subtle", "#7b8494");
    const head = readVar(wrap, "--gz-danger", "#d93636");

    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, width, height);

    const n = data.length;
    if (n > 0) {
      const barW = Math.max(1, width / n);
      const mid = height / 2;
      const progressX =
        durationSec > 0 ? (clamp(currentSec, 0, durationSec) / durationSec) * width : -1;
      for (let i = 0; i < n; i += 1) {
        const x = i * barW;
        const h = Math.max(1, (data[i] ?? 0) * (height - 4));
        ctx.fillStyle = x < progressX ? barPast : bar;
        ctx.fillRect(x, mid - h / 2, Math.max(1, barW - 0.5), h);
      }
      if (progressX >= 0) {
        ctx.fillStyle = head;
        ctx.fillRect(progressX, 0, 2, height);
      }
    }
  }, [data, width, height, currentSec, durationSec]);

  const seekFromClientX = (clientX: number) => {
    const el = wrapRef.current;
    if (!el || !onSeek || durationSec <= 0) return;
    const rect = el.getBoundingClientRect();
    const ratio = clamp((clientX - rect.left) / (rect.width || 1), 0, 1);
    onSeek(ratio * durationSec);
  };

  return (
    <div
      ref={wrapRef}
      className={cx("xz-waveform", onSeek && "is-seekable", className)}
      style={{ height }}
      role={onSeek ? "slider" : "img"}
      aria-label={label}
      aria-valuemin={onSeek ? 0 : undefined}
      aria-valuemax={onSeek ? Math.round(durationSec) : undefined}
      aria-valuenow={onSeek ? Math.round(currentSec) : undefined}
      tabIndex={onSeek ? 0 : undefined}
      onClick={(e) => seekFromClientX(e.clientX)}
      onKeyDown={(e) => {
        if (!onSeek) return;
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          onSeek(clamp(currentSec - 1, 0, durationSec));
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          onSeek(clamp(currentSec + 1, 0, durationSec));
        }
      }}
    >
      <canvas ref={canvasRef} className="xz-waveform__canvas" style={{ width: "100%", height }} />
    </div>
  );
}
